/**
 * Performs a deep equality check between two values.
 * Handles objects, arrays, dates, and primitive types.
 * 
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function deepEquals(a: any, b: any): boolean {
    // Handle same reference or identical primitives
    if (a === b) return true;

    // Handle null/undefined cases
    if (a == null || b == null) return a === b;

    // Handle different types
    if (typeof a !== typeof b) return false;

    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        
        for (let i = 0; i < a.length; i++) {
            if (!deepEquals(a[i], b[i])) return false;
        }
        
        return true;
    }

    // Handle objects
    if (typeof a === 'object' && typeof b === 'object') {
        // Get all keys from both objects
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);

        // Check if they have the same number of keys
        if (keysA.length !== keysB.length) return false;

        // Check if all keys and values match
        for (const key of keysA) {
            if (!keysB.includes(key)) return false;
            if (!deepEquals(a[key], b[key])) return false;
        }

        return true;
    }

    // For primitives that aren't ===
    return false;
}

export default deepEquals;

